import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useBookLoader } from '@/hooks/useBookLoader';
import { useYearbook } from '@/hooks/useYearbook';
import PreviewView from '../PreviewView';
import SubmitDialog from '../SubmitDialog';
import { Loader2 } from 'lucide-react';
import MobileTopBar from './MobileTopBar';
import MobilePageView, { getMobileSpreads, findSpreadIndex } from './MobilePageView';
import MobilePageStrip from './MobilePageStrip';
import MobileToolbar, { MobileTool } from './MobileToolbar';
import MobileToolDrawer from './MobileToolDrawer';
import { useEditorT } from '@i18n/editor';


const EditorLayoutMobile: React.FC = () => {
  const { te } = useEditorT();
  const {
    bookPages,
    initialLoadedIndices,
    trameTemplateInfos,
    trameBackgroundInfos,
    stickerInfos,
    coverInfos,
    coverName: initialCoverName,
    favoritesColors,
    pictureToUse: _initialPictureToUse,
    allFolders,
    plateforme,
    yearbookId,
    isLoading,
    defaultFont,
  } = useBookLoader();
  const isWememberz = plateforme === 'wememberz';

  if (isLoading || !bookPages) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-editor-canvas gap-4">
        <Loader2 className="animate-spin text-editor-sidebar-active" size={32} />
        <p className="text-xs text-muted-foreground">{te('Chargement…')}</p>
      </div>
    );
  }

  return (
    <MobileEditorContent
      initialPages={bookPages}
      preloadedIndices={initialLoadedIndices}
      trameTemplateInfos={trameTemplateInfos}
      trameBackgroundInfos={trameBackgroundInfos}
      stickerInfos={stickerInfos}
      coverInfos={coverInfos}
      initialCoverName={initialCoverName}
      initialFavoritesColors={favoritesColors}
      allFolders={allFolders}
      isWememberz={isWememberz}
      yearbookId={yearbookId}
      defaultFont={defaultFont}
    />
  );
};

interface ContentProps {
  initialPages: import('@/types/yearbook').YearbookPage[];
  preloadedIndices: Set<number>;
  trameTemplateInfos: ReturnType<typeof useBookLoader>['trameTemplateInfos'];
  trameBackgroundInfos: ReturnType<typeof useBookLoader>['trameBackgroundInfos'];
  stickerInfos: ReturnType<typeof useBookLoader>['stickerInfos'];
  coverInfos: ReturnType<typeof useBookLoader>['coverInfos'];
  initialCoverName: string;
  initialFavoritesColors: string[];
  allFolders: ReturnType<typeof useBookLoader>['allFolders'];
  isWememberz: boolean;
  yearbookId: number | null;
  defaultFont: string;
}

const MobileEditorContent: React.FC<ContentProps> = ({
  initialPages,
  preloadedIndices,
  trameTemplateInfos,
  trameBackgroundInfos,
  stickerInfos,
  coverInfos,
  initialCoverName,
  initialFavoritesColors,
  allFolders,
  isWememberz,
  yearbookId,
  defaultFont,
}) => {
  const {
    pages,
    currentPageIndex,
    setCurrentPageIndex,
    currentPage,
    selectedElementIds,
    selectedElement,
    selectedElements,
    addTextElement,
    addImageElement,
    addStickerElement,
    addShapeElement,
    updateElement,
    updateElementDrag,
    setPageBackground,
    setPageRichBackground,
    applyLayout,
    applyCover,
    undo,
    redo,
    canUndo,
    canRedo,
    beginDrag,
    endDrag,
    setSelectedElementIds,
    loadedIndices,
    ensureIndicesLoaded,
    loadAllPages,
    isSaving,
    deleteElement,
  } = useYearbook(initialPages, preloadedIndices, defaultFont);

  const [coverName, setCoverName] = useState<string>(initialCoverName);
  const [favColors, setFavColors] = useState<string[]>(initialFavoritesColors);
  const [submitOpen, setSubmitOpen] = useState(false);

  const [openTool, setOpenTool] = useState<MobileTool | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [mountedPanels, setMountedPanels] = useState<Set<MobileTool>>(() => new Set());
  const [viewMode, setViewMode] = useState<'single' | 'double'>('single');

  // Start on the front cover (pages are ordered [back, (tranche), front, ...])
  const didSetInitialPage = useRef(false);
  useEffect(() => {
    if (didSetInitialPage.current) return;
    didSetInitialPage.current = true;
    const frontIdx = pages.findIndex((p) => p.coverType === 'front');
    if (frontIdx > 0) setCurrentPageIndex(frontIdx);
  }, [pages, setCurrentPageIndex]);

  const editorRef = useRef<HTMLDivElement>(null);

  // The visible viewport can shrink independently of 100dvh (keyboard/browser UI).
  useEffect(() => {
    const viewport = window.visualViewport;
    const updateViewport = () => {
      const editor = editorRef.current;
      if (!editor) return;
      editor.style.setProperty('--mobile-viewport-height', `${viewport?.height ?? window.innerHeight}px`);
      editor.style.setProperty('--mobile-viewport-top', `${viewport?.offsetTop ?? 0}px`);
    };
    updateViewport();
    window.addEventListener('resize', updateViewport);
    viewport?.addEventListener('resize', updateViewport);
    viewport?.addEventListener('scroll', updateViewport);
    return () => {
      window.removeEventListener('resize', updateViewport);
      viewport?.removeEventListener('resize', updateViewport);
      viewport?.removeEventListener('scroll', updateViewport);
    };
  }, []);

  const isCoverPage = !!currentPage?.coverType;


  // Ensure page is loaded when navigating (and the next one in double-page mode)
  useEffect(() => {
    let indices = [currentPageIndex];
    if (viewMode === 'double') {
      const spreads = getMobileSpreads(pages);
      indices = Array.from(new Set([currentPageIndex, ...(spreads[findSpreadIndex(spreads, pages, currentPageIndex)] ?? [])]));
    }
    ensureIndicesLoaded(indices);
  }, [currentPageIndex, viewMode, pages, ensureIndicesLoaded]);

  const handleSelectPage = useCallback(
    (idx: number) => {
      setCurrentPageIndex(idx);
      setSelectedElementIds([]);
    },
    [setCurrentPageIndex, setSelectedElementIds]
  );

  const handleOpenTool = useCallback(
    (tool: MobileTool) => {
      if (tool === 'preview') {
        loadAllPages();
        setPreviewOpen(true);
        return;
      }
      setMountedPanels((prev) => {
        if (prev.has(tool)) return prev;
        const next = new Set(prev);
        next.add(tool);
        return next;
      });
      setOpenTool(tool);
    },
    [loadAllPages]
  );

  const handleSelectElement = useCallback(
    (id: string | null) => {
      setSelectedElementIds(id ? [id] : []);
      if (id && currentPage) {
        const el = currentPage.elements.find((e) => e.id === id);
        if (el && el.type === 'slot' && !el.imageUrl) {
          handleOpenTool('photos');
        }
      }
    },
    [setSelectedElementIds, currentPage, handleOpenTool]
  );


  const pictureToUse = useMemo(() => {
    const ids = new Set<number>();
    for (const page of pages) {
      for (const el of page.elements) {
        if (el.pictureId) ids.add(el.pictureId);
      }
    }
    return ids;
  }, [pages]);

  return (
    <div ref={editorRef} className="fixed inset-x-0 top-[var(--mobile-viewport-top,0px)] h-[var(--mobile-viewport-height,100dvh)] min-h-0 flex flex-col overflow-hidden bg-editor-canvas">
      <MobileTopBar
        onUndo={undo}

        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        onSubmit={() => setSubmitOpen(true)}
        isSaving={isSaving}
        isWememberz={isWememberz}
        selectedElement={selectedElement}
        onUpdateElement={updateElement}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onDeleteElement={(id) => {
          const el = currentPage?.elements.find(e => e.id === id);
          if (el?.type === 'slot' && el.imageUrl) {
            updateElement(id, { imageUrl: undefined, thumbnail: undefined, pictureId: undefined, imageOffset: undefined, imageScale: undefined, imageRatio: undefined });
          } else {
            deleteElement(id);
          }
          setSelectedElementIds([]);
        }}
      />

      <MobilePageView
        pages={pages}
        currentPageIndex={currentPageIndex}
        selectedElementIds={selectedElementIds}
        selectedElements={selectedElements}
        loadedIndices={loadedIndices}
        viewMode={viewMode}
        onSelectPage={handleSelectPage}
        onSelectElement={handleSelectElement}
        onUpdateElement={updateElement}
        onDragUpdateElement={updateElementDrag}
        onDragStart={beginDrag}
        onDragEnd={endDrag}
      />

      <MobilePageStrip
        pages={pages}
        currentPageIndex={currentPageIndex}
        onSelectPage={handleSelectPage}
        loadedIndices={loadedIndices}
        coverName={coverName}
      />


      <MobileToolbar isCoverPage={isCoverPage} onSelect={handleOpenTool} />

      <MobileToolDrawer
        openTool={openTool}
        onClose={() => setOpenTool(null)}
        isCoverPage={isCoverPage}
        isWememberz={isWememberz}
        onAddText={addTextElement}
        onAddImage={addImageElement}
        onAddSticker={addStickerElement}
        onAddShape={addShapeElement}
        onSetBackground={setPageBackground}
        onSetRichBackground={setPageRichBackground}
        onApplyLayout={applyLayout}
        onApplyCover={(data) => { applyCover(data); if (data.cover_name) setCoverName(data.cover_name); }}
        activeTemplateId={currentPage?.layoutTemplateId}
        activeBackgroundId={currentPage?.background?.richBackgroundId ?? currentPage?.background?.trame_background_id}
        activeBackgroundCustomId={currentPage?.background?.trame_background_custom_id}
        selectedElement={selectedElement}
        onUpdateElement={updateElement}
        trameTemplateInfos={trameTemplateInfos}
        trameBackgroundInfos={trameBackgroundInfos}
        stickerInfos={stickerInfos}
        coverInfos={coverInfos}
        favColors={favColors}
        onFavColorsChange={setFavColors}
        pictureToUse={pictureToUse}
        allFolders={allFolders}
        mountedPanels={mountedPanels}
      />

      {previewOpen && (
        <div className="fixed inset-0 z-[100] bg-editor-canvas flex flex-col">
          <div className="h-12 shrink-0 bg-editor-topbar border-b border-editor-sidebar-border flex items-center justify-between px-3">
            <span className="text-xs font-medium text-editor-sidebar-foreground">Aperçu</span>
            <button
              onClick={() => setPreviewOpen(false)}
              className="px-3 py-1.5 text-xs rounded-md bg-editor-sidebar-hover text-editor-sidebar-foreground"
            >
              Fermer
            </button>
          </div>
          <PreviewView pages={pages} singlePageMode />
        </div>
      )}

      <SubmitDialog
        open={submitOpen}
        onOpenChange={setSubmitOpen}
        isWememberz={isWememberz}
        yearbookId={yearbookId}
      />
    </div>
  );
};

export default EditorLayoutMobile;